import { Menu, Transition } from "@headlessui/react";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import Table from "components/Tablev2";
import { format } from "date-fns";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "react-use";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import ApiService from "services/api.service";
import { Workflow, EntityWithComputedFields } from "types/Workflow";
import NameWorkflowModal from "./Modals/NameWorkflowModal";
import threeDotsIcon from "./svg/three-dots-icon.svg";
import emptyDataImage from "./svg/empty-data.svg";
import sortAscChevronsImage from "./svg/sort-asc-chevrons.svg";
import sortDescChevronsImage from "./svg/sort-desc-chevrons.svg";
import sortNoneChevronsImage from "./svg/sort-none-chevrons.svg";
import Pagination from "components/Pagination";
import DeleteWorkflowModal from "./Modals/DeleteWorkflowModal";
import ToggleSwitch from "components/Elements/ToggleSwitch";

enum FilterOption {
  ALL,
  ACTIVE,
  DRAFT,
  PAUSED,
  STOPPED,
  ENROLLING,
}

const filterOptionToTextMap: Record<FilterOption, string> = {
  [FilterOption.ALL]: "All",
  [FilterOption.ACTIVE]: "Active",
  [FilterOption.DRAFT]: "Draft",
  [FilterOption.PAUSED]: "Paused",
  [FilterOption.STOPPED]: "Stopped",
  [FilterOption.ENROLLING]: "Enrolling",
};

const filterOptionsToRender: FilterOption[] = [
  FilterOption.ALL,
  FilterOption.ACTIVE,
  FilterOption.DRAFT,
  FilterOption.PAUSED,
  FilterOption.STOPPED,
  FilterOption.ENROLLING,
];

export type ChosenFilter =
  | FilterOption.ALL
  | Exclude<FilterOption, FilterOption.ALL>[];

interface WorkflowRowData {
  id: string;
  name: string;
  status: WorkflowStatus;
  enrolledCount: number;
  latestChangerEmail?: string;
  lastUpdate: string;
}

export enum WorkflowStatus {
  ACTIVE = "Active",
  PAUSED = "Paused",
  STOPPED = "Stopped",
  DELETED = "Deleted",
  DRAFT = "Draft",
  ENROLLING = "Enrolling",
}

export const workflowStatusClassName: Record<WorkflowStatus, string> = {
  [WorkflowStatus.ACTIVE]: "bg-[#DCFCE7] text-[#14532D]",
  [WorkflowStatus.DELETED]: "",
  [WorkflowStatus.ENROLLING]: "bg-[#FEF9C3] text-[#713F12]",
  [WorkflowStatus.DRAFT]: "bg-[#E0F2FE] text-[#0C4A6E]",
  [WorkflowStatus.PAUSED]: "bg-[#FEF9C3] text-[#713F12]",
  [WorkflowStatus.STOPPED]: "bg-[#F3F4F6] text-[#6B7280]",
};

const filterOptionToWorkflowStatusMap: Record<
  Exclude<FilterOption, FilterOption.ALL>,
  WorkflowStatus
> = {
  [FilterOption.ACTIVE]: WorkflowStatus.ACTIVE,
  [FilterOption.ENROLLING]: WorkflowStatus.ENROLLING,
  [FilterOption.DRAFT]: WorkflowStatus.DRAFT,
  [FilterOption.PAUSED]: WorkflowStatus.PAUSED,
  [FilterOption.STOPPED]: WorkflowStatus.STOPPED,
};

enum SortProperty {
  NAME = "name",
  STATUS = "status",
  ENROLLED_COUNT = "enrolledCount",
  LATEST_CHANGER_EMAIL = "latestChangerEmail",
  LAST_UPDATE = "latestSave",
}

enum SortType {
  ASC = "asc",
  DESC = "desc",
}

interface SortOptions {
  sortBy: SortProperty;
  sortType: SortType;
}

const DEFAULT_ITEMS_PER_PAGE = 20;
const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];

interface ActivationPreflightResult {
  ok: boolean;
  issues?: { message?: string }[];
}

export interface WorkflowsQueryParams {
  page: number;
  take: number;
  search: string;
  sortBy: SortProperty;
  sortType: SortType;
  filterStatuses: string;
}

// What the table shows on first open: page 1, no search, newest first, all
// statuses. The app-entry prefetch warms exactly this.
export const DEFAULT_WORKFLOWS_QUERY_PARAMS: WorkflowsQueryParams = {
  page: 1,
  take: DEFAULT_ITEMS_PER_PAGE,
  search: "",
  sortBy: SortProperty.LAST_UPDATE,
  sortType: SortType.DESC,
  filterStatuses: "",
};

// Single source of truth for the workflows list request — used by both the
// table page and the prefetch, so they resolve to the exact same cache entry.
export const buildWorkflowsQuery = (params: WorkflowsQueryParams) => ({
  queryKey: [
    "workflows",
    {
      page: params.page,
      take: params.take,
      search: params.search,
      sortBy: params.sortBy,
      sortType: params.sortType,
      filterStatuses: params.filterStatuses,
    },
  ],
  queryFn: async () => {
    const {
      data: { data: results, totalPages },
    } = await ApiService.get<{
      data: EntityWithComputedFields<Workflow>[];
      totalPages: number;
    }>({
      url: `/workflows?${new URLSearchParams({
        take: params.take.toString(),
        skip: ((params.page - 1) * params.take).toString(),
        search: params.search,
        orderBy: params.sortBy,
        orderType: params.sortType,
        ...(params.filterStatuses
          ? { filterStatuses: params.filterStatuses }
          : {}),
      }).toString()}`,
    });

    const rows: WorkflowRowData[] = results.map((workflowResult) => {
      const workflow = workflowResult.entity;
      const { computed } = workflowResult;

      let status: WorkflowStatus = WorkflowStatus.DRAFT;

      if (workflow.isActive) {
        if (workflow.isEnrolling) status = WorkflowStatus.ENROLLING;
        else status = WorkflowStatus.ACTIVE;
      }
      if (workflow.isPaused) status = WorkflowStatus.PAUSED;
      if (workflow.isStopped) status = WorkflowStatus.STOPPED;
      if (workflow.isDeleted) status = WorkflowStatus.DELETED;

      return {
        id: workflow.id,
        name: workflow.name,
        status,
        enrolledCount: computed.totalEnrolled || 0,
        lastUpdate: workflow.latestSave,
        latestChangerEmail: computed.latestChangerEmail,
      };
    });

    return { rows, totalPages };
  },
});

// Warm the default workflows view into the cache so the builder page renders
// instantly the first time it's opened in a session.
export const prefetchWorkflows = (queryClient: QueryClient) =>
  queryClient.prefetchQuery(
    buildWorkflowsQuery(DEFAULT_WORKFLOWS_QUERY_PARAMS)
  );

const WorkflowTablev2 = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [chosenFilter, setChosenFilter] = useState<ChosenFilter>(
    FilterOption.ALL
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNameWorkflowModalOpen, setIsNameWorkflowModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [workflowStatusChangingId, setWorkflowStatusChangingId] =
    useState<string>();

  const [currentPage, setCurrentPage] = useState(1);

  const [sortOptions, setSortOptions] = useState<SortOptions>({
    sortBy: SortProperty.LAST_UPDATE,
    sortType: SortType.DESC,
  });

  const [workflowToDelete, setWorkflowToDelete] = useState<string>();

  // Debounce the search box into the value the query actually uses, so typing
  // doesn't fire a request on every keystroke.
  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  const filterStatuses =
    chosenFilter === FilterOption.ALL
      ? ""
      : chosenFilter
          .map((filter) => filterOptionToWorkflowStatusMap[filter])
          .join(",");

  // Caps the enrolling poll: if a workflow stays stuck in "Enrolling" we stop
  // after 30s instead of polling forever.
  const enrollingSinceRef = useRef<number | null>(null);

  const { data, isPending, isError, isSuccess } = useQuery({
    // Shared factory so the page and the app-entry prefetch hit the same key.
    ...buildWorkflowsQuery({
      page: currentPage,
      take: itemsPerPage,
      search: debouncedSearch,
      sortBy: sortOptions.sortBy,
      sortType: sortOptions.sortType,
      filterStatuses,
    }),
    // Keep the current rows on screen while the next request runs, so paging,
    // filtering, and returning to the page never blank the table.
    placeholderData: keepPreviousData,
    // While any visible workflow is mid-enrollment, poll every 1.5s to catch
    // the isEnrolling true -> false flip from the enrollment processor.
    refetchInterval: (query) => {
      const polledRows = query.state.data?.rows;
      const stillEnrolling = polledRows?.some(
        (r) => r.status === WorkflowStatus.ENROLLING
      );
      if (!stillEnrolling) {
        enrollingSinceRef.current = null;
        return false;
      }
      if (enrollingSinceRef.current === null) {
        enrollingSinceRef.current = Date.now();
      }
      if (Date.now() - enrollingSinceRef.current > 30000) return false;
      return 1500;
    },
  });

  const rows = data?.rows ?? [];
  const pagesCount = data?.totalPages ?? 1;

  useEffect(() => {
    if (isError) toast.error("Failed to load data");
  }, [isError]);

  // Other parts of the app dispatch this event when they change workflows
  // (e.g. launching one). Invalidating refetches whatever the table shows.
  useEffect(() => {
    const onChange = () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    };
    window.addEventListener("leadstats:workflows-changed", onChange);
    return () =>
      window.removeEventListener("leadstats:workflows-changed", onChange);
  }, [queryClient]);

  useEffect(() => {
    setCurrentPage(1);
  }, [chosenFilter, debouncedSearch, sortOptions, itemsPerPage]);

  const handleChangeChosenFilter = (filterOption: FilterOption) => {
    if (filterOption === FilterOption.ALL) {
      setChosenFilter(FilterOption.ALL);
      return;
    }

    if (chosenFilter === FilterOption.ALL) {
      setChosenFilter([filterOption]);
      return;
    }

    const newChosenFilter = [...chosenFilter];
    if (chosenFilter.includes(filterOption)) {
      newChosenFilter.splice(newChosenFilter.indexOf(filterOption), 1);

      setChosenFilter(
        newChosenFilter.length > 0 ? newChosenFilter : FilterOption.ALL
      );

      return;
    }

    newChosenFilter.push(filterOption);

    setChosenFilter(newChosenFilter);
  };

  // Duplicate a workflow and insert the returned copy at the top of the current
  // page instead of re-running the heavy workflows list query.
  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      ApiService.post<Workflow>({ url: "/workflows/duplicate/" + id }),
    onSuccess: ({ data: wf }) => {
      if (!wf?.id) {
        // Server returned nothing usable; fall back to a refetch.
        queryClient.invalidateQueries({ queryKey: ["workflows"] });
        return;
      }
      let status: WorkflowStatus = WorkflowStatus.DRAFT;
      if (wf.isActive) {
        status = wf.isEnrolling
          ? WorkflowStatus.ENROLLING
          : WorkflowStatus.ACTIVE;
      }
      if (wf.isPaused) status = WorkflowStatus.PAUSED;
      if (wf.isStopped) status = WorkflowStatus.STOPPED;
      if (wf.isDeleted) status = WorkflowStatus.DELETED;
      const row: WorkflowRowData = {
        id: wf.id,
        name: wf.name,
        status,
        enrolledCount: 0,
        lastUpdate: wf.latestSave,
        latestChangerEmail: undefined,
      };
      const { queryKey } = buildWorkflowsQuery({
        page: currentPage,
        take: itemsPerPage,
        search: debouncedSearch,
        sortBy: sortOptions.sortBy,
        sortType: sortOptions.sortType,
        filterStatuses,
      });
      queryClient.setQueryData<{ rows: WorkflowRowData[]; totalPages: number }>(
        queryKey,
        (old) =>
          old
            ? { ...old, rows: [row, ...old.rows].slice(0, itemsPerPage) }
            : old
      );
    },
  });

  const duplicateWorkflow = (id: string) =>
    toast.promise(duplicateMutation.mutateAsync(id), {
      pending: "Duplicating workflow...",
      success: "Workflow duplicated",
      error: "Failed to duplicate workflow",
    });

  const showEmptyState =
    rows.length === 0 &&
    chosenFilter === FilterOption.ALL &&
    search === "" &&
    isSuccess;

  const handleSort = (sortBy: SortProperty) => {
    setSortOptions((current) => {
      if (current.sortBy !== sortBy) {
        return {
          sortBy,
          sortType:
            sortBy === SortProperty.LAST_UPDATE ||
            sortBy === SortProperty.ENROLLED_COUNT
              ? SortType.DESC
              : SortType.ASC,
        };
      }

      return {
        sortBy,
        sortType:
          current.sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
      };
    });
  };

  const sortIcon = (sortBy: SortProperty) =>
    sortOptions.sortBy === sortBy
      ? sortOptions.sortType === SortType.ASC
        ? sortAscChevronsImage
        : sortDescChevronsImage
      : sortNoneChevronsImage;

  const sortableHeading = (label: string, sortBy: SortProperty) => (
    <button
      type="button"
      className="flex items-center gap-[2px] select-none"
      onClick={() => handleSort(sortBy)}
    >
      <span>{label}</span>
      <img src={sortIcon(sortBy)} />
    </button>
  );

  const workflowStatusSwitchChecked = (status: WorkflowStatus) =>
    status === WorkflowStatus.ACTIVE || status === WorkflowStatus.ENROLLING;

  const workflowStatusSwitchDisabled = (row: WorkflowRowData) =>
    workflowStatusChangingId === row.id ||
    row.status === WorkflowStatus.ENROLLING ||
    row.status === WorkflowStatus.DELETED;

  const lifecycleErrorMessage = (error: any, fallback: string) => {
    const message = error?.response?.data?.message;
    if (Array.isArray(message)) return message[0] || fallback;
    return message || error?.message || fallback;
  };

  const handleWorkflowStatusToggle = async (
    row: WorkflowRowData,
    checked: boolean
  ) => {
    const action =
      row.status === WorkflowStatus.DRAFT && checked
        ? "start"
        : row.status === WorkflowStatus.STOPPED && checked
        ? "start"
        : row.status === WorkflowStatus.ACTIVE && !checked
        ? "pause"
        : row.status === WorkflowStatus.PAUSED && checked
        ? "resume"
        : undefined;

    if (!action || workflowStatusChangingId) return;

    setWorkflowStatusChangingId(row.id);
    try {
      if (action === "start") {
        const preflightResponse =
          await ApiService.get<ActivationPreflightResult>({
            url: `/workflows/${row.id}/preflight`,
          });
        if (!preflightResponse.data.ok) {
          throw new Error(
            preflightResponse.data.issues?.[0]?.message ||
              "Workflow cannot be started until its setup is complete."
          );
        }
      }

      await ApiService.patch({ url: `/workflows/${action}/${row.id}` });
      await queryClient.invalidateQueries({ queryKey: ["workflows"] });
      window.dispatchEvent(new CustomEvent("leadstats:workflows-changed"));
      toast.success(
        action === "start"
          ? "Workflow started"
          : action === "pause"
          ? "Workflow paused"
          : "Workflow resumed"
      );
    } catch (error) {
      toast.error(
        lifecycleErrorMessage(
          error,
          action === "start"
            ? "Failed to start workflow"
            : action === "pause"
            ? "Failed to pause workflow"
            : "Failed to resume workflow"
        )
      );
    } finally {
      setWorkflowStatusChangingId(undefined);
    }
  };

  return (
    <div className="bg-themed-background p-5 flex flex-col gap-5 font-inter font-normal text-[14px] text-themed-text leading-[22px]">
      <div className="flex justify-between">
        <div className="text-[20px] font-semibold leading-[28px]">
          Workflows
        </div>
        <Button
          type={ButtonType.PRIMARY}
          onClick={() => setIsNameWorkflowModalOpen(true)}
          id="create-workflow"
        >
          Create workflow
        </Button>
      </div>
      <div className="p-5 rounded-lg bg-themed-card flex flex-col gap-5">
        {showEmptyState ? (
          <div className="w-full h-[300px] flex items-center justify-center select-none">
            <div className="flex flex-col items-center gap-5">
              <img src={emptyDataImage} />

              <div className="font-inter text-[16px] font-semibold leading-[24px] text-themed-textSecondary">
                Create a workflow to engage your contacts
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex flex-wrap gap-[10px]">
                {filterOptionsToRender.map((filterOption, i) => (
                  <button
                    className={`px-[12px] py-[5px] rounded ${
                      (chosenFilter === FilterOption.ALL &&
                        filterOption === FilterOption.ALL) ||
                      (chosenFilter !== FilterOption.ALL &&
                        filterOption !== FilterOption.ALL &&
                        chosenFilter.includes(filterOption))
                        ? "border border-[#6366F1] bg-[#EEF2FF] text-[#6366F1]"
                        : ""
                    }`}
                    onClick={() => handleChangeChosenFilter(filterOption)}
                    key={i}
                  >
                    {filterOptionToTextMap[filterOption]}
                  </button>
                ))}
              </div>

              <Input
                value={search}
                onChange={setSearch}
                placeholder="Search workflows"
                showClearButton
                wrapperClassName="w-full sm:w-[280px]"
                className="w-full !bg-themed-input"
              />
            </div>

            <Table
              isLoading={isPending}
              headings={[
                sortableHeading("Name", SortProperty.NAME),
                sortableHeading("Status", SortProperty.STATUS),
                sortableHeading(
                  "Enrolled contacts",
                  SortProperty.ENROLLED_COUNT
                ),
                sortableHeading(
                  "Last updated by",
                  SortProperty.LATEST_CHANGER_EMAIL
                ),
                sortableHeading("Last update", SortProperty.LAST_UPDATE),
                <div className="px-5 py-[10px] select-none"></div>,
              ]}
              rowsData={rows}
              rows={rows.map((row) => [
                <button
                  className="text-[#6366F1]"
                  onClick={() =>
                    navigate(
                      row.status === WorkflowStatus.DRAFT
                        ? "/flow/" + row.id
                        : "/flow/" + row.id + "/view"
                    )
                  }
                >
                  {row.name}
                </button>,
                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    checked={workflowStatusSwitchChecked(row.status)}
                    disabled={workflowStatusSwitchDisabled(row)}
                    iconRequired={false}
                    ariaLabel={`Change ${row.name} workflow status`}
                    onChange={(checked) =>
                      handleWorkflowStatusToggle(row, checked)
                    }
                  />
                  <span
                    className={`px-[10px] py-[2px] rounded-[14px] w-fit ${
                      workflowStatusClassName[row.status]
                    }`}
                  >
                    {row.status}
                  </span>
                </div>,
                <div>
                  {row.status === WorkflowStatus.DRAFT &&
                  row.enrolledCount === 0 ? (
                    "-"
                  ) : (
                    <>
                      {row.enrolledCount}{" "}
                      {row.enrolledCount == 1 ? "contact" : "contacts"}
                    </>
                  )}
                </div>,
                <div>{row.latestChangerEmail || "-"}</div>,
                <div>
                  {format(new Date(row.lastUpdate), "MM/dd/yyyy HH:mm")}
                </div>,
                <Menu as="div" className="relative">
                  <Menu.Button>
                    <button className="px-[5px] py-[11px] rounded">
                      <img className="row-actions-icon" src={threeDotsIcon} />
                    </button>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute z-[120] right-0 origin-top-right w-[200px] h-[108px] py-[4px] rounded-sm bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`block w-full text-left py-[5px] px-[12px] ${
                              active ? "bg-themed-background" : ""
                            }`}
                            onClick={() =>
                              navigate(
                                row.status === WorkflowStatus.DRAFT
                                  ? "/flow/" + row.id
                                  : "/flow/" + row.id + "/view"
                              )
                            }
                          >
                            {row.status === WorkflowStatus.DRAFT
                              ? "Edit"
                              : "View"}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`block w-full text-left py-[5px] px-[12px] ${
                              active ? "bg-themed-background" : ""
                            }`}
                            onClick={() => duplicateWorkflow(row.id)}
                          >
                            Duplicate
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`block w-full text-left py-[5px] px-[12px] text-[#F43F5E] ${
                              active ? "bg-themed-background" : ""
                            }`}
                            onClick={() => setWorkflowToDelete(row.id)}
                          >
                            Delete
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>,
              ])}
            />
          </>
        )}

        {!showEmptyState && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-[150px]" />
            <div className="flex justify-center items-center">
              {pagesCount > 1 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalPages={pagesCount}
                />
              )}
            </div>
            <label className="flex items-center justify-end gap-2 text-[13px]">
              <span className="text-themed-textSecondary">Rows per page</span>
              <select
                value={itemsPerPage}
                className="rounded border border-themed-border bg-themed-card px-2 py-1 text-themed-text outline-none focus:border-[#6366F1]"
                onChange={(event) => setItemsPerPage(+event.target.value)}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      <NameWorkflowModal
        isOpen={isNameWorkflowModalOpen}
        onClose={() => setIsNameWorkflowModalOpen(false)}
      />

      <DeleteWorkflowModal
        isOpen={!!workflowToDelete}
        workflowId={workflowToDelete}
        onClose={() => setWorkflowToDelete(undefined)}
      />
    </div>
  );
};

export default WorkflowTablev2;
